import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { SecurityGroups, SecurityGroupMembers } from 'src/app/shared/securitygroups';
import { ClientApiService } from 'src/app/services/client-api.service';
import { SecureApiService } from 'src/app/services/secure-api.service';

/**
 * Reusable security-group dropdown selector backed by `SecureApiService`.
 *
 * On init the component reads the caller's access level from `GetglobalsettingsService` and fetches
 * the full `SecurityGroups` list, then filters it to entries whose `SECGROUPID` is ≤ the caller's
 * own level so users cannot assign a group higher than their own.  The initial selection is resolved
 * from `selectedID` after the list is loaded.
 *
 * On `selectedID` input change the component re-resolves `selectedGroup` from the already-loaded
 * list without a new HTTP call.
 *
 * **Output:** `selectedSecurityGroup` emits the full `SecurityGroups` object on `groupEditClick`.
 */
@Component({
  selector: 'app-security-group-selector',
  templateUrl: './security-group-selector.component.html',
  styleUrls: ['./security-group-selector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SecurityGroupSelectorComponent implements OnInit, OnChanges, OnDestroy {

  private securityQuery: Subscription;

  /** The caller's own access-group level, read from `GetglobalsettingsService` on init. */
  userAccessGroup: number;

  /** Full filtered list of security groups (≤ caller's level). */
  securityGroups: SecurityGroups[];
  /** Currently resolved `SecurityGroups` object matching `selectedID`. */
  selectedGroup: SecurityGroups;

  /** Label text displayed above the dropdown. */
  @Input() labelKey = 'Группы доступа';
  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder = 'Выбрать групу';

  /** The ID of the selected security group (`SecurityGroups.SECGROUPID`). Triggers re-resolution on change. */
  @Input()
  get selectedID(): number { return this._selectedID; }
  set selectedID(id: number) {
    this._selectedID = id;
  }
  private _selectedID: number = null;

  /** When `true`, the dropdown is rendered as read-only. */
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(b: boolean) {
    this._disabled = b;
  }
  _disabled: boolean = false;

  /** Emits the full `SecurityGroups` object when the user confirms a selection via `groupEditClick`. */
  @Output()
  public selectedSecurityGroup: EventEmitter<SecurityGroups> = new EventEmitter();

  constructor(
    private secApi: SecureApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /** Reacts to `selectedID` changes by re-resolving `selectedGroup` from the cached list. */
  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** SecurityGroupSelectorComponent ngOnChanges');
    for (let propName in changes) {
      let change = changes[propName];
      console.log('*** SecurityGroupSelectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
      //console.log('*** SecurityGroupSelectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'selectedID':
          if (this.securityGroups) {
            this.selectedGroup = this.securityGroups.find(e => e.SECGROUPID == change.currentValue);
          }
          break;
      }
    }
  }

  /** Unsubscribes the security-group HTTP subscription to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** SecurityGroupSelectorComponent ngOnDestroy');
    if (this.securityQuery) { this.securityQuery.unsubscribe(); }
  }

  /**
   * Reads the caller's access level, fetches and filters the security-group list, then resolves
   * the initial selection from `selectedID`.
   */
  ngOnInit(): void {
    console.log('*** SecurityGroupSelectorComponent ngOnInit');

    this.userAccessGroup = this.globalsettingsservice.getAccessGroup();

    this.securityQuery =
        this.secApi.getSecurityGroupList()
          .pipe(
            map((data: SecurityGroups[]) => {
              this.securityGroups = data.filter(e => e.SECGROUPID <= this.userAccessGroup);
              this.selectedGroup = this.securityGroups.find(e => e.SECGROUPID == this.selectedID);
              console.log('*** SecurityGroupSelectorComponent onInit: in map');
            }))
        .subscribe(() => {
          console.log('*** SecurityGroupSelectorComponent onInit: in subscription');
        });
  }

  /** Handles dropdown click/select — emits `selectedGroup` via `selectedSecurityGroup`. */
  groupEditClick(event) {
    console.log('*** SecurityGroupSelectorComponent groupEditClick');
    this.selectedSecurityGroup.emit(this.selectedGroup);
}

  /** Handles dropdown `(onChange)` — currently a no-op. */
  groupEditChange(event){
    console.log('*** SecurityGroupSelectorComponent groupEditChange');
  }

}
